import React, { useEffect } from "react";
import "./Settings.scss";

// Hooks and Services
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiService } from "../../../services";

// Context
import { useSettings } from "../../../contexts/SettingsContext";

// Components
import { IonButton, IonCol, IonContent, IonFooter, IonRow, IonSpinner, IonToast } from "@ionic/react";
import SettingsSegment from "../../../components/Admin/Settings/SettingsSegment";
import { AdminTemplate } from "../../../components/PageTemplates";
import Brand from "./Brand/Brand";
import Emails from "./Emails/Emails";
import GlobalWording from "./GlobalWording/GlobalWording";
import Miscellaneous from "./Miscellaneous/Miscellaneous";
import Sponsors from "./Sponsors/Sponsors";

// Types
import { defaultSettings } from "../../../defaultSettings";
import { CurrentSegmentState, FormDataState } from "./SettingsTypes";

const defaultGlobalWordings = {
  app_name: defaultSettings.global_wording.app_name,
  section_chat: defaultSettings.global_wording.section_chat,
  section_latest: defaultSettings.global_wording.section_latest,
  section_registration: defaultSettings.global_wording.section_registration,
  section_register: defaultSettings.global_wording.section_register,
  section_drive: defaultSettings.global_wording.section_drive,
  section_calendar: defaultSettings.global_wording.section_calendar,
  entity_members: defaultSettings.global_wording.entity_members,
  misc_registration: defaultSettings.global_wording.misc_registration,
  misc_profile: defaultSettings.global_wording.misc_profile,
  misc_picture: defaultSettings.global_wording.misc_picture,
  misc_admins: defaultSettings.global_wording.misc_admins,
  misc_channel: defaultSettings.global_wording.misc_channel,
};

const defaultFormData: FormDataState = {
  brand: {},
  sponsors: {},
  global_wording: {
    ...defaultGlobalWordings,
  },
  email: {},
  miscellaneous: {},
};

export default function Settings() {
  const [currentSegment, setCurrentSegment] = useState<CurrentSegmentState>("brand");
  const [disableSaveButton, setDisableSaveButton] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormDataState>(defaultFormData);
  const [errors, setErrors] = useState<any>({});
  const [notification, setNotification] = useState<any>();
  const [loading, setLoading] = useState<boolean>(true);
  const { settings, refreshSettings } = useSettings();

  const { t } = useTranslation();

  useEffect(() => {
    ApiService.get("settings").then((response) => {
      Object.keys(response).forEach((key) => {
        Object.keys(response[key]).forEach((subKey) => {
          if (response[key][subKey] === null) {
            delete response[key][subKey];
          }
        });
      });
      setFormData(response);
      setLoading(false);
    });
  }, []);

  return (
    <AdminTemplate className="page--admin page--admin--settings">
      <IonContent>
        <IonRow>
          <IonCol size="6" className="page--admin--settings__content">
            <div className="wrapper">
              <h1>{t("settings")}</h1>
              <SettingsSegment currentSegment={currentSegment} updateSegment={updateSegment} />
              {loading ? <IonSpinner /> : renderCurrentSegment()}
            </div>
          </IonCol>

          <IonCol size="6" className="page--admin--settings__preview">
            <img src={settings?.brand?.img_path_admin_header_logo || "assets/logo-white.svg"} alt={`${t("instance")} logo`} width="130px" />
          </IonCol>
        </IonRow>
      </IonContent>

      <IonFooter className="wrapper">
        <IonButton onClick={saveSettings} className="button--primary button--block" disabled={disableSaveButton || loading}>
          {t("save")}
        </IonButton>
      </IonFooter>

      {notification && <IonToast isOpen={true} position="top" onDidDismiss={() => setNotification(null)} message={notification.message} color={notification.color} duration={5000} />}
    </AdminTemplate>
  );

  function updateSegment(event: CustomEvent) {
    setCurrentSegment(event.detail.value);
  }

  function renderCurrentSegment() {
    switch (currentSegment) {
      case "brand":
        return <Brand formData={formData} updateFormData={updateFormData} errors={errors} />;
      case "sponsors":
        return <Sponsors formData={formData} updateFormData={updateFormData} errors={errors} />;
      case "global-wording":
        return <GlobalWording formData={formData} updateFormData={updateFormData} errors={errors} defaults={defaultGlobalWordings} />;
      case "emails":
        return <Emails formData={formData} updateFormData={updateFormData} errors={errors} />;
      case "miscellaneous":
        return <Miscellaneous formData={formData} updateFormData={updateFormData} errors={errors} />;
    }
  }

  function updateFormData(event: any) {
    const formDataTemp: FormDataState = formData;
    let fieldName = event.target?.name || event.detail?.name;
    const value = event?.srcElement?.type === "toggle" ? (event.detail?.checked ? "true" : "false") : event.detail?.value;

    if (!fieldName) {
      fieldName = event.srcElement?.name;
    }

    if (fieldName.includes(".")) {
      const fieldNameSplit = fieldName.split(".");
      // @ts-ignore
      formDataTemp[fieldNameSplit[0]][fieldNameSplit[1]] = value;
    } else {
      // @ts-ignore
      formDataTemp[fieldName] = value;
    }
    setFormData(formDataTemp);
  }

  function constructPayload() {
    const payload = new FormData();
    Object.keys(formData).forEach((key) => {
      // @ts-ignore
      Object.keys(formData[key]).forEach((subKey) => {
        // @ts-ignore
        if (subKey.includes("img") && typeof formData[key][subKey] === "string") {
          // @ts-ignore
          delete formData[key][subKey];
        } else {
          // @ts-ignore
          payload.append(`${key}[${subKey}]`, formData[key][subKey]);
        }
      });
    });

    return payload;
  }

  function saveSettings() {
    setDisableSaveButton(true);
    setErrors(defaultFormData);
    const payload = constructPayload();

    return ApiService.post(`settings/?_method=PUT`, payload, undefined, true)
      .then((response) => {
        setDisableSaveButton(false);
        refreshSettings(response);
        setNotification({ message: "Settings saved", color: "success" });
      })
      .catch((response) => {
        setErrors(response.errors);
        setDisableSaveButton(false);
        setNotification({ message: "Error saving settings", color: "danger" });
      });
  }
}
